import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { motion } from "framer-motion";
import { Trophy, GraduationCap, Sparkles } from "lucide-react";

function medal(rank) {
  if (rank === 1) return { emoji: "①", tint: "bg-[#F4D06F] text-[#4A3B00] border-[#EBBB4A]" };
  if (rank === 2) return { emoji: "②", tint: "bg-[#DDDCD5] text-[#3A3A3A] border-[#B9B7AC]" };
  if (rank === 3) return { emoji: "③", tint: "bg-[#E3B48D] text-[#4A2C10] border-[#C7906A]" };
  return { emoji: `#${rank}`, tint: "bg-brand-bone text-brand-muted border-brand-linen" };
}

export default function Leaderboard() {
  const { user } = useAuth();
  const [me, setMe] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/leaderboard");
        setMe(data.me);
        setRows(data.rows);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <Layout>
      <div className="max-w-4xl mx-auto">
        <div className="flex items-end justify-between gap-4 mb-10 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-[0.28em] text-brand-muted mb-2">Your practice</div>
            <h1 className="font-serif text-5xl text-brand-ink">Leaderboard</h1>
            <p className="text-brand-body mt-2">Points = 100 per quiz passed + 10 per lesson completed. Practice-only.</p>
          </div>
          {me && (
            <div className="bg-brand-ink text-brand-bone rounded-3xl px-6 py-4 shadow-card" data-testid="leaderboard-me">
              <div className="text-[11px] uppercase tracking-widest text-brand-bone/70">You</div>
              <div className="font-serif text-2xl mt-1">Rank {me.rank} · {me.points} pts</div>
            </div>
          )}
        </div>

        {loading && <div className="text-brand-muted italic">Loading…</div>}

        {!loading && rows.length === 0 && (
          <div className="border border-dashed border-brand-linen rounded-3xl p-10 text-center text-brand-muted">
            No learners yet. Invite your team to start earning points.
          </div>
        )}

        {!loading && rows.length > 0 && (
          <div className="bg-white border border-brand-linen rounded-3xl shadow-card overflow-hidden" data-testid="leaderboard-list">
            <div className="grid grid-cols-[auto_1fr_auto_auto_auto] gap-4 px-6 py-3 border-b border-brand-linen text-[11px] uppercase tracking-widest text-brand-muted">
              <div>Rank</div><div>Learner</div><div className="text-right">Lessons</div><div className="text-right">Passes</div><div className="text-right">Points</div>
            </div>
            {rows.map((r, i) => {
              const m = medal(r.rank);
              const isMe = user?.id === r.user_id;
              return (
                <motion.div
                  key={r.user_id}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.35, delay: Math.min(i, 8) * 0.03 }}
                  data-testid="leaderboard-row"
                  data-rank={r.rank}
                  className={`grid grid-cols-[auto_1fr_auto_auto_auto] gap-4 px-6 py-4 border-b border-brand-linen last:border-b-0 items-center ${isMe ? "bg-brand-bone/60" : ""}`}
                >
                  <span className={`inline-flex items-center justify-center w-9 h-9 border rounded-full text-xs font-mono ${m.tint}`}>{m.emoji}</span>
                  <div className="min-w-0">
                    <div className="font-medium text-brand-ink truncate flex items-center gap-2">
                      {r.name}
                      {isMe && <span className="text-[10px] uppercase tracking-widest text-brand-secondary bg-brand-secondary/10 border border-brand-secondary/20 rounded-full px-2 py-0.5">You</span>}
                      {r.role === "admin" && <span className="text-[10px] uppercase tracking-widest text-brand-muted bg-brand-linen rounded-full px-2 py-0.5">PM</span>}
                    </div>
                  </div>
                  <div className="text-sm text-brand-body text-right font-mono">{r.lessons}</div>
                  <div className="text-sm text-brand-body text-right font-mono">{r.passes}</div>
                  <div className="text-brand-ink font-serif text-xl text-right">{r.points}</div>
                </motion.div>
              );
            })}
          </div>
        )}

        <div className="mt-8 flex items-center gap-3 text-sm text-brand-muted">
          <Trophy className="w-4 h-4 text-brand-accent" strokeWidth={1.7} />
          Tip: every completed lesson earns 10 pts and every quiz pass earns 100. Certificates don&apos;t decay — but neither does your position.
        </div>
      </div>
    </Layout>
  );
}
